import {
    el, mount, setStyle, setAttr
} from 'redom';
import remove from 'lodash/remove';
import { MouseCatcher } from '../Catchers/mouseCatcher';
import { MeshCatcher } from '../Catchers/meshCatcher';
import { SceneryEvironment } from '../Scenery/sceneryEnvironment';
import { SceneryPipeline } from '../Scenery/sceneryPipeline';
import { ContentsManager, DEFAULT_MODEL_OPTION } from '../Content/contentsManager';
import { SystemUi } from '../System/systemUi';
import { SceneryCamera } from '../Scenery/sceneryCamera';
import { ToolScreenshot } from '../Tool/toolScreenshot';
import { IModelLink, IStudioOptions } from './interface';
import { getQueryParam, getUrlWithoutExtension } from '../../Utils/html';

const checkElWithStyle = (el: HTMLElement) => (el.style && !el.style.zIndex) && (el.tagName && el.tagName !== 'SCRIPT');

/**
    * Window viewport can be problematic for scale rendering, we check if one is present
    * @ignore
    */
const checkViewport = () => {
    // In order to have good scale and text size, we need to check for the viewport meta in header
    // This makes the scene a bit blurry on iphones, need to find a solution
    const viewport = document.querySelector('meta[name=viewport]');
    if (!viewport) {
        const viewporttoadd = el('meta', { content: 'width=device-width, initial-scale=1', name: 'viewport' });
        mount(document.getElementsByTagName('head')[0], viewporttoadd);
    }
    // Should check for the viewport and adapt to it.
    // else {
    //   let content = viewport.getAttribute("content");
    //   if (content) {
    //     let scalecheck = content.indexOf('initial-scale');
    //     if (scalecheck === -1) this.ratio = window.devicePixelRatio;
    //   }
    // }
};

const getFirstModelUrl = (contents): string => {
    let modelUrl = '';
    contents.forEach((content) => {
        if (content.type === 'model') modelUrl = content.typedata.url;
    });
    return modelUrl;
};

/**
 * This is the main class regrouping all of your project data and object
 *
 * Studio is returned from the render function
 * This is also the first and only argument of the callback of that same function
 */

export class EngineBuilder {
    protected container: HTMLElement;

    public sceneContainer: HTMLElement;

    private canvas: HTMLCanvasElement;

    public system: SystemUi;

    protected meshCatcher: MeshCatcher;

    private mouseCatcher: MouseCatcher;

    public contentsManager: ContentsManager;

    private sceneryPipeline: SceneryPipeline;

    protected sceneryCamera: SceneryCamera;

    private sceneryEvironment: SceneryEvironment;

    public toolScreenshot: ToolScreenshot

    public options: IStudioOptions;

    constructor(studioOptions: IStudioOptions) {
        if (!studioOptions.container) throw Error('Naker : Missing container');
        if (studioOptions.container.tagName === 'HEAD') throw Error('Naker : container can not be head tag');
        this.container = studioOptions.container;
        this.options = studioOptions;

        // let browser = this.getBrowser();
        //   let canvasposition = (browser === 'Safari') ? '-webkit-sticky' : 'sticky';
        // overflow hidden can break some website
        // setStyle(this.container, {
        //     'overflow-x': 'hidden',
        //     '-webkit-tap-highlight-color':
        //     'transparent',
        //     'background-color': 'rgba(0,0,0,0)'
        // });
        setStyle(this.container, { '-webkit-tap-highlight-color': 'transparent' });
        for (let i = 0; i < this.container.childNodes.length; i++) {
            const child = this.container.childNodes[i];
            if (checkElWithStyle(child as HTMLElement)) {
                // Some node can't change style
                try { setStyle(child, { 'z-index': '1' }); } catch (e) {
                    console.error('Naker : Cannot set zIndex');
                }
            }
        }

        this.sceneContainer = el('div', {
            class: 'naker-scene-container',
            style: {
                top: '0px', left: '0px', width: '100%', height: '100%', 'overflow-y': 'hidden', 'overflow-x': 'hidden'
            },
        });
        this.sceneContainer.addEventListener('contextmenu', () => false);
        mount(this.container, this.sceneContainer);
        this.canvas = el('canvas', {
            class: 'naker-canvas',
            style: {
                top: '0px', left: '0px', width: '100%', height: '100%', 'overflow-y': 'hidden', 'overflow-x': 'hidden', outline: 'none', 'touch-action': 'none'
            }
        });
        let canvasposition = 'absolute';
        if (this.container === document.body) canvasposition = 'fixed';
        setStyle(this.canvas, { position: canvasposition });
        // if (this.container === document.body) setStyle(this.container, {	'overflow-y': 'auto' });
        // Problem with scroll and touch action with android, discussion here:
        // https://forum.babylonjs.com/t/scroll-issues-with-touch-action/2135
        // 'touch-action': 'none' Keep it to avoid refresh on android phones
        // -webkit-tap to avoid touch effect on iphone

        setAttr(this.canvas, { 'data-who': 'Made with naker.io' });
        mount(this.sceneContainer, this.canvas);

        this.checkContainerPosition();
        checkViewport();
        if (studioOptions && studioOptions.listenEvent === false) this.setNoEvent();
    }

    private setNoEvent() {
        setStyle(this.container, { 'pointer-events': 'none' });
    }

    /**
    * Make sure there is a position on container
    * @ignore
    */
    private checkContainerPosition() {
        // Due to a lot of user feedback having trouble when no position on parent
        // .style returns only inline values (not useful)
        // let containerStyle = this.container.style;
        // getComputedStyle return all values so can be used to check if position already setted
        const containerStyle = window.getComputedStyle(this.container);
        // Best way found to determine if there is already a position or not
        // Seems like static is the default value (tested in edge, chrome and firefox)
        if (containerStyle.position === 'static') {
            // We set to relative because this is the default behavior
            setStyle(this.container, { position: 'relative' });
        }
    }

    protected projectId: string;

    protected buildEngine(studioOptions: IStudioOptions): void {
        this.projectId = studioOptions.id;
        this.buildSystem();
        this.meshCatcher = new MeshCatcher(this.system);
        this.mouseCatcher = new MouseCatcher(this.system);
        this.contentsManager = new ContentsManager(this.system, this.mouseCatcher);
        this.sceneryEvironment = new SceneryEvironment(this.system);
        this.sceneryPipeline = new SceneryPipeline(this.system);
        this.sceneryCamera = new SceneryCamera(this.system, this.mouseCatcher);
        this.toolScreenshot = new ToolScreenshot(this.system, this.sceneryPipeline);
        this.system.toolMaterial.materialList = studioOptions.materials;
    }

    protected buildSystem(): void {
        if (this.system) return;
        this.system = new SystemUi(this.canvas);
    }

    /**
     * @ignore Property Rights
     */
    protected buildProject(studioOptions: IStudioOptions): void {
        this.buildEngine(studioOptions);
        this.setSceneOptions(studioOptions);
        this.addAllProjectContents(studioOptions.contents);
    }

    protected _modelLink: IModelLink;

    private set modelLink(v : IModelLink) {
        this._modelLink = v;
        if (!v.usdz) {
            const urlWithoutExtension = getUrlWithoutExtension(v.glb);
            this._modelLink.usdz = `${urlWithoutExtension}usdz`;
        }
        this._modelLink.glb = decodeURIComponent(this._modelLink.glb);
        this._modelLink.usdz = decodeURIComponent(this._modelLink.usdz);
    }

    protected checkModelLink(studioOptions: IStudioOptions): void {
        const glbFromQuery = getQueryParam('glbUrl');
        const usdzFromQuery = getQueryParam('usdzUrl');
        if (studioOptions.modelLink) {
            this.modelLink = studioOptions.modelLink;
        } else if (glbFromQuery) {
            this.modelLink = { glb: glbFromQuery, usdz: usdzFromQuery };
        } else {
            const glbUrl = getFirstModelUrl(studioOptions.contents);
            this.modelLink = { glb: glbUrl, usdz: null };
        }

        if (glbFromQuery) {
            remove(studioOptions.contents, (c) => c.type === 'model');
            studioOptions.contents.push({
                type: 'model',
                contentdata: DEFAULT_MODEL_OPTION,
                typedata: { url: decodeURIComponent(glbFromQuery) }
            });
        }
    }

    public loadModelFromUrl(glbUrl: string, usdzUrl?: string): void {
        this.modelLink = { glb: glbUrl, usdz: usdzUrl };
        this.contentsManager.destroyModels();
        const mod = this.contentsManager.addModel(DEFAULT_MODEL_OPTION, {});
        mod.show();
        mod.loadModel(glbUrl, () => {
            this.system.quickRender();
        });
    }

    protected startRendering(callback?: () => void): void {
        this.contentsManager.checkOptimization();
        this.contentsManager.checkIfAnimations();

        // Do not optimizeHard because freezeActiveMeshes
        // is broking the activeCameras and depthofField
        // We can optimize hard here as we do not use depthofField in studio
        // Plus we win 10% of performance when freezeActiveMeshes is ON so very powerful
        this.system.optimizeHard();
        this.system.setLimitFPS(true);

        // showContents is called before and after launchRender or it doesn't show
        this.contentsManager.showContents();
        this.system.launchRender(() => {
            // Force light shadows to be calculated once everything is ready
            this.contentsManager.showLights();
            this.mouseCatcher.start();
            this.sceneryCamera.resetInitialView();
            if (callback) callback();
        });
    }

    /**
     * @ignore Property Rights
     */
    private setSceneOptions(project) {
        this.sceneryCamera.setOption(project.camera);
        this.sceneryPipeline.checkOption(project.pipeline);
        // Keep environment at last to make sure fog density is full
        this.sceneryEvironment.checkOption(project.environment);
    }

    /**
     * @ignore Property Rights
     */
    private addAllProjectContents(contentsdata) {
        this.contentsManager.setContents(contentsdata);
        // Need to create animation at the if animation has a target
        this.addAllAnimations(contentsdata);
        this.addAllVariants(contentsdata);
    }

    /**
     * @ignore Property Rights
     */
    private addAllAnimations(contentsdata) {
        contentsdata.forEach((contentdata) => {
            const { id } = contentdata.contentdata;
            if (contentdata.animations !== undefined) {
                this.contentsManager.addContentAnimation(id, contentdata.animations);
            }
        });
    }

    /**
    * @ignore Property Rights
    */
    private addAllVariants(contentsdata) {
        contentsdata.forEach((contentdata) => {
            const { id } = contentdata.contentdata;
            if (contentdata.variants !== undefined && contentdata.variants.length) {
                this.contentsManager.addContentVariants(id, contentdata.variants);
            }
        });
    }

    public disposeProject(): void {
        this.contentsManager.destroyContents();
        this.system.stopRender();
        this.system.disposeElements();
    }
}
